// Type definitions for Teacher Application Form

export interface TeacherApplicationForm {
  // Step 1: Personal Information
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  province_id: number;
  city_id: number;
  street_name: string;
  street_number: string;
  zip: string;

  // Step 2: Professional Information
  has_car: boolean;
  qualifications: string;
  current_employment: string;

  // Step 3: Subjects
  subjects: SubjectsAndSchools[];

  // Step 4: Weekly Availability (56 fields - 7 days × 8 time slots)
  // Monday
  monday_availability_morning: boolean;
  monday_availability_13_14: boolean;
  monday_availability_14_15: boolean;
  monday_availability_15_16: boolean;
  monday_availability_16_17: boolean;
  monday_availability_17_18: boolean;
  monday_availability_18_19: boolean;
  monday_availability_19_20: boolean;

  // Tuesday
  tuesday_availability_morning: boolean;
  tuesday_availability_13_14: boolean;
  tuesday_availability_14_15: boolean;
  tuesday_availability_15_16: boolean;
  tuesday_availability_16_17: boolean;
  tuesday_availability_17_18: boolean;
  tuesday_availability_18_19: boolean;
  tuesday_availability_19_20: boolean;

  // Wednesday
  wednesday_availability_morning: boolean;
  wednesday_availability_13_14: boolean;
  wednesday_availability_14_15: boolean;
  wednesday_availability_15_16: boolean;
  wednesday_availability_16_17: boolean;
  wednesday_availability_17_18: boolean;
  wednesday_availability_18_19: boolean;
  wednesday_availability_19_20: boolean;

  // Thursday
  thursday_availability_morning: boolean;
  thursday_availability_13_14: boolean;
  thursday_availability_14_15: boolean;
  thursday_availability_15_16: boolean;
  thursday_availability_16_17: boolean;
  thursday_availability_17_18: boolean;
  thursday_availability_18_19: boolean;
  thursday_availability_19_20: boolean;

  // Friday
  friday_availability_morning: boolean;
  friday_availability_13_14: boolean;
  friday_availability_14_15: boolean;
  friday_availability_15_16: boolean;
  friday_availability_16_17: boolean;
  friday_availability_17_18: boolean;
  friday_availability_18_19: boolean;
  friday_availability_19_20: boolean;

  // Saturday
  saturday_availability_morning: boolean;
  saturday_availability_13_14: boolean;
  saturday_availability_14_15: boolean;
  saturday_availability_15_16: boolean;
  saturday_availability_16_17: boolean;
  saturday_availability_17_18: boolean;
  saturday_availability_18_19: boolean;
  saturday_availability_19_20: boolean;

  // Sunday
  sunday_availability_morning: boolean;
  sunday_availability_13_14: boolean;
  sunday_availability_14_15: boolean;
  sunday_availability_15_16: boolean;
  sunday_availability_16_17: boolean;
  sunday_availability_17_18: boolean;
  sunday_availability_18_19: boolean;
  sunday_availability_19_20: boolean;
}

export interface SubjectsAndSchools {
  id: number;
  schools: SchoolAndClasses[];
}

export interface SchoolAndClasses {
  id: number;
  classes: string[];
}

// Helper types for availability management
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export type TimeSlot =
  | 'morning'
  | '13_14'
  | '14_15'
  | '15_16'
  | '16_17'
  | '17_18'
  | '18_19'
  | '19_20';

export const TIME_SLOTS: TimeSlot[] = [
  'morning',
  '13_14',
  '14_15',
  '15_16',
  '16_17',
  '17_18',
  '18_19',
  '19_20',
];

export const DAYS_OF_WEEK: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const TIME_SLOT_LABELS: Record<TimeSlot, string> = {
  morning: 'Mattina (08:00-13:00)',
  '13_14': '13:00-14:00',
  '14_15': '14:00-15:00',
  '15_16': '15:00-16:00',
  '16_17': '16:00-17:00',
  '17_18': '17:00-18:00',
  '18_19': '18:00-19:00',
  '19_20': '19:00-20:00',
};

export const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Lunedì',
  tuesday: 'Martedì',
  wednesday: 'Mercoledì',
  thursday: 'Giovedì',
  friday: 'Venerdì',
  saturday: 'Sabato',
  sunday: 'Domenica',
};

// Helper function to generate availability field name
export function getAvailabilityFieldName(day: DayOfWeek, timeSlot: TimeSlot): keyof TeacherApplicationForm {
  return `${day}_availability_${timeSlot}` as keyof TeacherApplicationForm;
}

// Initial form data with all fields set to default values
export const getInitialFormData = (): TeacherApplicationForm => ({
  // Step 1
  email: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  province_id: 0,
  city_id: 0,
  street_name: '',
  street_number: '',
  zip: '',

  // Step 2
  has_car: false,
  qualifications: '',
  current_employment: '',

  // Step 3
  subjects: [],

  // Step 4 - All availability fields default to false
  monday_availability_morning: false,
  monday_availability_13_14: false,
  monday_availability_14_15: false,
  monday_availability_15_16: false,
  monday_availability_16_17: false,
  monday_availability_17_18: false,
  monday_availability_18_19: false,
  monday_availability_19_20: false,

  tuesday_availability_morning: false,
  tuesday_availability_13_14: false,
  tuesday_availability_14_15: false,
  tuesday_availability_15_16: false,
  tuesday_availability_16_17: false,
  tuesday_availability_17_18: false,
  tuesday_availability_18_19: false,
  tuesday_availability_19_20: false,

  wednesday_availability_morning: false,
  wednesday_availability_13_14: false,
  wednesday_availability_14_15: false,
  wednesday_availability_15_16: false,
  wednesday_availability_16_17: false,
  wednesday_availability_17_18: false,
  wednesday_availability_18_19: false,
  wednesday_availability_19_20: false,

  thursday_availability_morning: false,
  thursday_availability_13_14: false,
  thursday_availability_14_15: false,
  thursday_availability_15_16: false,
  thursday_availability_16_17: false,
  thursday_availability_17_18: false,
  thursday_availability_18_19: false,
  thursday_availability_19_20: false,

  friday_availability_morning: false,
  friday_availability_13_14: false,
  friday_availability_14_15: false,
  friday_availability_15_16: false,
  friday_availability_16_17: false,
  friday_availability_17_18: false,
  friday_availability_18_19: false,
  friday_availability_19_20: false,

  saturday_availability_morning: false,
  saturday_availability_13_14: false,
  saturday_availability_14_15: false,
  saturday_availability_15_16: false,
  saturday_availability_16_17: false,
  saturday_availability_17_18: false,
  saturday_availability_18_19: false,
  saturday_availability_19_20: false,

  sunday_availability_morning: false,
  sunday_availability_13_14: false,
  sunday_availability_14_15: false,
  sunday_availability_15_16: false,
  sunday_availability_16_17: false,
  sunday_availability_17_18: false,
  sunday_availability_18_19: false,
  sunday_availability_19_20: false,
});
